import React, { useEffect } from 'react';
import { useAuth } from './AuthContext';

/**
 * Converts a hex color to HSL format for CSS variables.
 * This allows compatibility with the shadcn/ui color system.
 */
function hexToHSL(hex: string): string {
  // Remove the hash if present
  hex = hex.replace(/^#/, '');

  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Return as "H S% L%" format (without 'deg' for CSS compatibility)
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Determines if a color is "light" to choose appropriate foreground color.
 */
function isLightColor(hex: string): boolean {
  hex = hex.replace(/^#/, '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

interface TenantThemeProviderProps {
  children: React.ReactNode;
}

/**
 * TenantThemeProvider applies tenant-specific branding via CSS custom properties.
 * It watches the tenant object from AuthContext and updates CSS variables accordingly.
 */
export const TenantThemeProvider: React.FC<TenantThemeProviderProps> = ({ children }) => {
  const { tenant } = useAuth();

  useEffect(() => {
    const root = document.documentElement;

    if (tenant?.primaryColor) {
      const primaryHSL = hexToHSL(tenant.primaryColor);
      root.style.setProperty('--primary', primaryHSL);

      // Set appropriate foreground color based on primary color brightness
      if (isLightColor(tenant.primaryColor)) {
        root.style.setProperty('--primary-foreground', '0 0% 9%'); // Dark text
      } else {
        root.style.setProperty('--primary-foreground', '0 0% 98%'); // Light text
      }

      // Also update chart colors to use primary
      root.style.setProperty('--chart-1', primaryHSL);
    }

    if (tenant?.secondaryColor) {
      const secondaryHSL = hexToHSL(tenant.secondaryColor);
      root.style.setProperty('--secondary', secondaryHSL);

      // Set appropriate foreground color based on secondary color brightness
      if (isLightColor(tenant.secondaryColor)) {
        root.style.setProperty('--secondary-foreground', '0 0% 9%'); // Dark text
      } else {
        root.style.setProperty('--secondary-foreground', '0 0% 98%'); // Light text
      }

      // Also update accent to match secondary
      root.style.setProperty('--accent', secondaryHSL);
      if (isLightColor(tenant.secondaryColor)) {
        root.style.setProperty('--accent-foreground', '0 0% 9%');
      } else {
        root.style.setProperty('--accent-foreground', '0 0% 98%');
      }
    }

    // Cleanup function to reset to defaults when tenant changes
    return () => {
      // We don't reset here to avoid flashing - the new tenant colors will overwrite
    };
  }, [tenant?.primaryColor, tenant?.secondaryColor]);

  return <>{children}</>;
};

export default TenantThemeProvider;
